'use client';

import { useEffect, useRef } from 'react';
import { getCookie } from 'cookies-next';
import parseUserV2 from 'utils/parseUserV2';
import useAppContext from 'lib/hooks/useAppContext';

export default function BrazeInitializer() {
  const lastEmailRef = useRef<string | null>(null);
  const { deviceType, pathname } = useAppContext();

  useEffect(() => {
    let brazeService: Awaited<
      typeof import('services/braze.service')
    >['default'];

    import('services/braze.service').then(({ default: BrazeService }) => {
      brazeService = BrazeService;
      BrazeService.getInstance().subscribeToEmailCapture(deviceType, pathname);
      BrazeService.getInstance().logTestPageviewEvent();

      // Capture the initial email state
      const userToken = getCookie('user_token');
      const user = parseUserV2(userToken as string | undefined);
      lastEmailRef.current = user.email as string | null; // Safety: we know this to be correct from parseUserV2 but TS can't infer it
    });

    // Poll for user_token cookie changes every 5 seconds to detect login changes
    // skip polling if the tab isn't active
    const interval = setInterval(() => {
      if (document.hidden) {
        return;
      }

      const userToken = getCookie('user_token');
      const user = parseUserV2(userToken as string | undefined);
      const currentEmail = user.email;

      if (
        currentEmail &&
        currentEmail !== lastEmailRef.current &&
        brazeService
      ) {
        brazeService.getInstance().changeUser(user);
        lastEmailRef.current = currentEmail;
      }

      if (!currentEmail && currentEmail !== lastEmailRef.current) {
        lastEmailRef.current = null;
      }
    }, 5000);

    return () => clearInterval(interval);
    // deviceType and pathname are intentionally excluded — we only want
    // the mount-time values for the one-time Braze subscription.
    // This is a similar approach to how we handle MixPanel props in useTrackPageViewEffect.ts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}
